// ADR 15.9.26ag §4 — a `<button>` around one `Icon`, and nothing else.
//
// `label` is **required**: an icon-only button with no name is a control a
// screen reader announces as "button", so the name is decided here rather
// than remembered at each call site. It becomes both `aria-label` (the
// accessible name) and `title` (the pointer user's tooltip). The `Icon`
// inside stays decorative — the button is what is named.
//
// `FOCUS_RING` and the 44px target from birth: `min-h-11 min-w-11` is the
// smaller of Apple's and Material's minimums, the same floor `Button` took in
// 15.9.26af §5, and the transition is that file's spelling verbatim.

import { forwardRef, type ButtonHTMLAttributes } from 'react'

import { FOCUS_RING, PRESSED_STATE } from '../Button'
import { Icon, type IconName } from '../icons'

export type IconButtonVariant = 'ghost' | 'tonal'

/**
 * Exported for ADR 24.9.26l AC12's target-size floor, which reads it.
 *
 * `PRESSED_STATE` (ADR 16.10.26k §1): a toggle shows it is on by the fill;
 * the weight and underline utilities are inert on an icon. A `tonal` toggle
 * would differ by nothing — the first one gets its own pressed cue.
 */
export const ICON_BUTTON_BASE =
  'inline-flex min-h-11 min-w-11 items-center justify-center rounded-full ' +
  'motion-safe:transition-colors duration-short ease-standard ' +
  `${FOCUS_RING} disabled:opacity-50 ${PRESSED_STATE}`

const VARIANT: Record<IconButtonVariant, string> = {
  ghost: 'text-ink-muted hover:bg-surface-sunken active:bg-line/60',
  // M3 `FilledTonalIconButton`: the secondary container (ADR 28.9.26b §6).
  tonal:
    'bg-secondary-soft text-on-secondary-soft hover:bg-secondary-soft/80 active:bg-secondary-soft/60',
}

export interface IconButtonProps
  extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'children' | 'aria-label' | 'title'> {
  icon: IconName
  /** The accessible name and the tooltip. Callers pass a translated string. */
  label: string
  variant?: IconButtonVariant
}

/**
 * The ref reaches the `<button>` (ADR 16.9.26r §2): the tutor dock moves focus
 * to its ✕ on open and the FAB takes it back on close, and neither can do so
 * through a component that swallows the ref.
 */
export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(function IconButton(
  { icon, label, variant = 'ghost', className = '', type, ...rest },
  ref,
) {
  return (
    <button
      ref={ref}
      type={type ?? 'button'}
      aria-label={label}
      title={label}
      className={`${ICON_BUTTON_BASE} ${VARIANT[variant]}${className === '' ? '' : ` ${className}`}`}
      {...rest}
    >
      <Icon name={icon} />
    </button>
  )
})
